import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  StarIcon,
  BriefcaseIcon,
  UserIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  RocketLaunchIcon,
  HandThumbUpIcon,
  EyeIcon,
  Battery50Icon,
  LightBulbIcon,
  CursorArrowRaysIcon,
  BeakerIcon,
  Cog8ToothIcon,
  HandRaisedIcon,
  ShareIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  ArrowLongRightIcon,
} from "@heroicons/react/24/solid";
import type { ComponentType } from "react";
import trpc from "../../../utils/trpc.js";
import type { TrpcOutput } from "../../../utils/trpc.js";
import { buildItemLink } from "../../../utils/canvas.js";
import { IconLeadingBusiness } from "../../../components/icons.generated";
import { Button } from "../../../components/button/button";

type ProfileData = Extract<NonNullable<TrpcOutput["profile"]["getProfile"]>, { template: "garrla-1" }>["data"];

// ─── Content data types ───────────────────────────────────────────────────────

type WorkLevel = 2 | 3 | 4;

type BehaviorInfo = {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
};

type PillarData = {
  blockBadge: string;
  pillarTitle: string;
  description: Record<WorkLevel, string>;
  perform: BehaviorInfo;
  transform: BehaviorInfo;
};

// ─── Static content maps ──────────────────────────────────────────────────────

const BLOCK2_CONTENT: Record<"LS" | "LE", PillarData> = {
  LS: {
    blockBadge: "Leading Business",
    pillarTitle: "Leading Strategy",
    description: {
      2: "Here, you will continue to build on your existing strengths within the Leading Strategy pillar. Your development will focus on strengthening each of the Perform and Transform behaviours, and your ability to move between these two modes, so you can respond effectively to both immediate demands and longer-term opportunities.",
      3: "Here, you will strengthen your overall ambidextrous leadership profile within the Leading Strategy pillar. The focus is on further developing your Perform and Transform capabilities and building the balance needed to move effectively between immediate operational demands and longer-term strategic opportunities.",
      4: "Here, you will strengthen your overall ambidextrous leadership profile within the Leading Strategy pillar. The focus is on further developing your Perform and Transform capabilities and building the balance needed to move effectively between immediate operational demands and longer-term strategic opportunities.",
    },
    perform: {
      title: "Operator",
      description:
        "Identifies short-term market opportunities using current business models and manages the pace of change.",
      icon: WrenchScrewdriverIcon,
    },
    transform: {
      title: "Transformer",
      description:
        "Questions the status quo and existing business models, and takes a long-term perspective on value creation.",
      icon: RocketLaunchIcon,
    },
  },
  LE: {
    blockBadge: "Leading Business",
    pillarTitle: "Leading Execution",
    description: {
      2: "Here, you will continue to build on your existing strengths within the Leading Execution pillar. Your development will focus on strengthening each of the Perform and Transform behaviours, and your ability to move between these two modes, so you can deliver with excellence while also experimenting and exploring new ways forward.",
      3: "Here, you will strengthen your overall ambidextrous leadership profile within the Leading Execution pillar. The focus is on further developing your Perform and Transform capabilities and building the balance needed to deliver with excellence while also experimenting with and exploring new ways forward.",
      4: "Here, you will strengthen your overall ambidextrous leadership profile within the Leading Execution pillar. The focus is on further developing your Perform and Transform capabilities and building the balance needed to deliver with excellence while also experimenting with and exploring new ways forward.",
    },
    perform: {
      title: "Implementor",
      description:
        "Focuses on execution and tracking progress, anticipates problems, plans contingencies, and solves unanticipated issues.",
      icon: CursorArrowRaysIcon,
    },
    transform: {
      title: "Experimenter",
      description:
        "Seeks new ways to deliver results, navigates ambiguity, and reshapes the existing business model to gain agility and flexibility.",
      icon: BeakerIcon,
    },
  },
};

const BLOCK3_CONTENT: Record<"LP" | "LSt", PillarData> = {
  LP: {
    blockBadge: "Leading People",
    pillarTitle: "Leading People",
    description: {
      2: "Here, you will harness your strengths within the Leading People pillar. Your development will focus on strengthening each of the Perform and Transform behaviours, knowing when to direct and when to empower and enable others.",
      3: "Within the Leading People pillar, you will strengthen your overall ambidextrous leadership profile. The emphasis is on developing your ability to coach and empower others, while also providing direction and managing performance when needed.",
      4: "Within the Leading People pillar, you will strengthen your overall ambidextrous leadership profile. The emphasis is on developing your ability to coach and empower others, while also providing direction and managing performance when needed.",
    },
    perform: {
      title: "Conductor",
      description:
        "Communicates a clear vision and plan, and leads from the front to direct actions and track performance.",
      icon: HandRaisedIcon,
    },
    transform: {
      title: "Coach",
      description: "Builds a trusting, empowering team culture, and leads by guiding and developing others.",
      icon: HandThumbUpIcon,
    },
  },
  LSt: {
    blockBadge: "Leading People",
    pillarTitle: "Leading Stakeholders",
    description: {
      2: "Here, you will harness your strengths within the Leading Stakeholders pillar. Your development will focus on strengthening each of the Perform and Transform behaviours, and your ability to move between these two modes, knowing when to work through formal authority and when to lead through influence.",
      3: "Within the Leading Stakeholders pillar, you will strengthen your overall ambidextrous leadership profile. The emphasis is on developing the capabilities needed to work through formal authority and lead through influence, while strengthening your ability to draw on each approach as situations require.",
      4: "Within the Leading Stakeholders pillar, you will strengthen your overall ambidextrous leadership profile. The emphasis is on developing the capabilities needed to work through formal authority and lead through influence, while strengthening your ability to draw on each approach as situations require.",
    },
    perform: {
      title: "Administrator",
      description: "Knows how to use formal structures and power sources to reach consensus and drive change.",
      icon: Cog8ToothIcon,
    },
    transform: {
      title: "Networker",
      description: "Reads and understands others, and uses informal power to create alignment.",
      icon: ShareIcon,
    },
  },
};

// ─── Journey card descriptions ────────────────────────────────────────────────

const JOURNEY_DESCRIPTION: Record<WorkLevel, string> = {
  2: "At this stage of your leadership journey, the priority is to build on what you already do well. By leaning into your strengths, you can develop confidence and a clear, recognisable organisational identity that will serve as a strong foundation for your future growth.\n\n Explore your learning journey below. Each pillar contains monthly sprints tailored to your development needs.",
  3: "At this stage of your leadership journey, the priority is to strengthen your overall ambidextrous leadership profile. This may involve building greater balance between Perform and Transform behaviours where an imbalance exists, or elevating areas where you already demonstrate balance but further growth is possible.\n\n Explore your learning journey below. Each pillar contains monthly sprints tailored to your development needs.",
  4: "At this stage of your leadership journey, the priority is to develop a broader ambidextrous leadership profile. The focus is on strengthening areas that are currently less prominent in your profile, helping you build the balance and versatility needed to respond effectively to a wider range of leadership challenges.\n\n Explore your learning journey below. Each pillar contains monthly sprints tailored to your development needs.",
};

// ─── Tab config ───────────────────────────────────────────────────────────────

type TabId = 1 | 2 | 3 | 4;

type TabConfig = {
  id: TabId;
  label: string;
  blockLabel: string;
  Icon: ComponentType<{ className?: string }>;
  disabled?: boolean;
};

const TABS: TabConfig[] = [
  { id: 1, label: "Getting Started", blockLabel: "Programme Launch", Icon: StarIcon },
  { id: 2, label: "Leading Business", blockLabel: "Pillar 1", Icon: BriefcaseIcon },
  { id: 3, label: "Leading People", blockLabel: "Pillar 2", Icon: UsersIcon },
  { id: 4, label: "Development Review", blockLabel: "Pillar 3", Icon: StarIcon, disabled: true },
];

// ─── Layout ───────────────────────────────────────────────────────────────────

export function Garrla1Layout({ data }: { data: ProfileData }) {
  return (
    <section className="space-y-14">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-[#1A1E2E]">Welcome</h1>
        <p className="mt-4 leading-relaxed text-[#43576B]">
          Welcome to your personalised development journey, the final stage of your Talent Accelerator programme with
          IMD. You now have a clearer picture of your leadership performance and potential. This learning experience
          builds on those insights, helping you focus your development where it can have the greatest impact.
        </p>
      </div>

      {/* Mastering Opposing Behaviours */}
      <div className="space-y-4">
        <div className="flex flex-col gap-10 md:flex-row">
          <div className="space-y-4 md:flex-[3]">
            <h2 className="text-2xl font-bold text-[#1A1E2E]">Mastering Opposing Behaviours</h2>
            <p className="leading-relaxed text-[#43576B]">
              Today&rsquo;s leaders face a defining challenge: the need to extract value from the current business while
              simultaneously creating new sources of value for the future. Meeting this requires ambidexterity: the
              ability to master both Perform and Transform behaviours across your leadership domains. Leaders who can do
              both are better placed to navigate complexity and deliver high performance, now and in the future.
            </p>
            <p className="leading-relaxed text-[#43576B]">
              Your Ambidex Diagnostics and Future Success Benchmark results provide two complementary perspectives on
              this capability. The Ambidex Diagnostics captures how you see your own ability to balance Perform and
              Transform behaviours, while the Future Success Benchmark reflects how your Transform behaviours are
              experienced by others.
            </p>
          </div>
          <div className="flex items-center justify-center md:flex-[2]">
            <AmbidexterityVennDiagram />
          </div>
        </div>
        <p className="leading-relaxed text-[#43576B]">
          Viewed together, these perspectives create an opportunity for deeper reflection on your development
          priorities. As you progress through your learning journey, continue to consider what the relationship between
          these perspectives might reveal about your opportunities for growth.
        </p>
      </div>
      {/* Future Success Benchmark items */}
      <FutureSuccessBenchmarkItems />
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-[#1A1E2E]">Personalised Learning</h2>
        <p className="leading-relaxed text-[#43576B]">
          Your personalised learning journey has been designed using the combined insights from these assessments and
          tailored to the leadership challenges and capabilities associated with your work level. The journey combines
          development across two core leadership domains, Leading Business and Leading People, alongside a dedicated
          leadership stream focused on either Leading Self or Adaptable Leadership. Together, these elements are
          designed to help you strengthen the capabilities most relevant to your development as an ambidextrous leader.
        </p>
      </div>
      <YourFeedback data={data.personalisedFeedback} />
      {/* Development Journey card */}
      <DevelopmentJourneyCard data={data} />
    </section>
  );
}

// ─── Development Journey card ─────────────────────────────────────────────────

// Finds the link to the first quiz module item across the user's course, if any.
function useFirstQuizLink(): string | null {
  const { data: modules } = trpc.course.getModules.useQuery(undefined, { staleTime: Infinity });

  return useMemo(() => {
    for (const module of modules ?? []) {
      for (const sub of module.subModules ?? []) {
        for (const item of sub.items) {
          if (item.type === "Assignment" && item.quiz_lti === true) {
            return buildItemLink(item);
          }
        }
      }
    }
    return null;
  }, [modules]);
}

function DevelopmentJourneyCard({ data }: { data: ProfileData }) {
  const [activeTab, setActiveTab] = useState<TabId>(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<TabId, HTMLButtonElement | null>>(new Map());
  const firstQuizLink = useFirstQuizLink();

  useEffect(() => {
    const activeBtn = tabRefs.current.get(activeTab);
    const container = scrollRef.current;
    if (activeBtn && container) {
      const scrollTarget = activeBtn.offsetLeft - container.clientWidth / 2 + activeBtn.offsetWidth / 2;
      container.scrollTo({ left: scrollTarget, behavior: "smooth" });
    }
  }, [activeTab]);

  return (
    <div className="overflow-hidden rounded-2xl bg-white p-8 shadow-lg shadow-lac-leman/10 md:p-16">
      {/* Card header */}
      <div>
        <div className="inline-flex rounded-full bg-lac-leman/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-lac-leman">
          Tailored to You
        </div>
        <h2 className="mt-4 text-2xl font-bold text-[#1A1E2E]">Your Development Journey</h2>
        <p className="mt-3 whitespace-pre-line leading-relaxed text-[#43576B]">
          {JOURNEY_DESCRIPTION[data.workLevel as WorkLevel]}
        </p>

        {/* Stepper / tab bar */}
        <div
          className="relative mt-12 overflow-x-auto pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          ref={scrollRef}>
          <div className="relative flex min-w-[700px]">
            {/* Dashed connector line spanning between the first and last diamond centres */}
            <div className="absolute left-[12.5%] right-[12.5%] top-5 border-t-2 border-dashed border-lac-leman/30" />
            {TABS.map(tab => (
              <button
                key={tab.id}
                ref={el => {
                  tabRefs.current.set(tab.id, el);
                }}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`relative flex flex-1 flex-col items-center gap-2 pb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman ${
                  tab.disabled ? "cursor-not-allowed" : ""
                }`}>
                {/* Diamond icon */}
                <div
                  className={`relative z-10 flex h-10 w-10 rotate-45 items-center justify-center rounded-lg transition-colors ${
                    tab.disabled ? "bg-gray-40" : "bg-lac-leman"
                  }`}>
                  <tab.Icon className="h-5 w-5 -rotate-45 text-white" />
                </div>
                <span
                  className={`text-center font-bold leading-tight transition-colors ${
                    tab.disabled ? "text-gray-60" : activeTab === tab.id ? "text-[#1A1E2E]" : "text-gray-80"
                  }`}>
                  {tab.label}
                </span>
                <span className="mb-8 text-xs text-gray-60">{tab.blockLabel}</span>
                {/* Active-tab underline indicator */}
                {!tab.disabled && activeTab === tab.id && (
                  <motion.div
                    layoutId="u1-tab-indicator"
                    className="absolute bottom-0 left-0 h-1 w-full bg-lac-leman"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gray" />

      {/* Tab content */}
      <motion.div
        className="py-16"
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}>
        {activeTab === 1 && <LeadershipBehavioursTabContent />}
        {activeTab === 2 && (
          <PillarTabContent
            pillar={BLOCK2_CONTENT[data.block1.dimension]}
            workLevel={data.workLevel as WorkLevel}
            BadgeIcon={BriefcaseIcon}
          />
        )}
        {activeTab === 3 && (
          <PillarTabContent
            pillar={BLOCK3_CONTENT[data.block2.dimension]}
            workLevel={data.workLevel as WorkLevel}
            BadgeIcon={UsersIcon}
          />
        )}
        {activeTab === 4 && <DevReviewTabContent />}
      </motion.div>

      {/* Divider */}
      <div className="h-px bg-gray" />

      {/* Leadership stream */}
      <LeadershipStream stream={data.stream} />

      {firstQuizLink && (
        <div className="mt-8 flex justify-end">
          <Button to={firstQuizLink} filled="lac-leman" className="flex items-center justify-center gap-2 px-5 py-2.5">
            <span className="flex items-center gap-2">
              Begin Diagnostic
              <ArrowLongRightIcon className="h-4 w-4" />
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Tab content panels ───────────────────────────────────────────────────────

function LeadershipBehavioursTabContent() {
  return (
    <div>
      <h3 className="mt-4 text-2xl font-bold text-[#1A1E2E]">Getting Started</h3>
      <p className="mt-3 leading-relaxed text-[#43576B]">
        The programme begins with a series of preparation activities designed to lay the foundation for your development
        journey. Professor Heather Cairns-Lee kicks off the programme with a Live Masterclass exploring the science of
        behaviour change and the role habits play in leadership development. You will clarify your Business Improvement
        Goal, identify the leadership habits that will support your success, and exchange experiences and ideas with
        colleagues who are on a similar development journey.
      </p>
    </div>
  );
}

function DevReviewTabContent() {
  return <p className="py-4 text-center text-[#43576B]">Your development review details will be available soon.</p>;
}

function PillarTabContent({
  pillar,
  workLevel,
  BadgeIcon,
}: {
  pillar: PillarData;
  workLevel: WorkLevel;
  BadgeIcon: ComponentType<{ className?: string }>;
}) {
  return (
    <div>
      {/* Block badge */}
      <div className="inline-flex items-center gap-2 rounded-full bg-lac-leman/10 px-3 py-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-lac-leman">{pillar.blockBadge}</span>

        <BadgeIcon className="h-4 w-4 text-lac-leman" />
      </div>

      <h3 className="mt-4 text-2xl font-bold text-[#1A1E2E]">{pillar.pillarTitle}</h3>
      <p className="mt-3 leading-relaxed text-[#43576B]">{pillar.description[workLevel]}</p>

      {/* Perform / Transform behaviour cards */}
      <div className="mt-6 flex flex-col md:flex-row md:items-stretch">
        <BehaviorCard
          Icon={pillar.perform.icon}
          iconColor="#1AAE9A"
          modeLabel="Perform"
          title={pillar.perform.title}
          description={pillar.perform.description}
        />
        {/* Dashed connector between cards */}
        <div className="flex shrink-0 items-center justify-center self-center py-1 md:px-1">
          <div className="h-8 border-l-2 border-dashed border-lac-leman/30 md:h-auto md:w-8 md:border-l-0 md:border-t-2" />
        </div>
        <BehaviorCard
          Icon={pillar.transform.icon}
          iconColor="#5B4BAF"
          modeLabel="Transform"
          title={pillar.transform.title}
          description={pillar.transform.description}
        />
      </div>
    </div>
  );
}

function BehaviorCard({
  Icon,
  iconColor,
  modeLabel,
  title,
  description,
}: {
  Icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
  iconColor: string;
  modeLabel: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3 rounded-xl border border-gray bg-white p-4 md:flex-row md:items-center md:gap-4">
      <div className="flex shrink-0 flex-row items-center gap-3 md:flex-col md:items-center md:gap-1 md:pt-1">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gray bg-white">
          <Icon className="h-6 w-6 text-white" style={{ color: iconColor }} />
        </div>
        <span className="mt-1 text-xs text-gray-60">{modeLabel}</span>
      </div>
      <div>
        <h4 className="font-bold text-[#1A1E2E]">{title}</h4>
        <p className="mt-1 leading-relaxed text-[#43576B]">{description}</p>
      </div>
    </div>
  );
}

// ─── Future Success Benchmark ────────────────────────────────────────────────

const FSB_ITEMS = [
  {
    Icon: HandThumbUpIcon,
    title: "Where you are aligned on your strengths",
    subtitle: "How will you leverage this capability?",
  },
  {
    Icon: EyeIcon,
    title: "Where your perceived strengths are not yet visible to others",
    subtitle: "How will you close that gap?",
  },
  {
    Icon: Battery50Icon,
    title: "Where both perspectives point to a development need",
    subtitle: "How will you address it?",
  },
  {
    Icon: LightBulbIcon,
    title: "Where you may be underestimating your capabilities",
    subtitle: "How will you build confidence in what you are already demonstrating to others?",
  },
] as const;

function FutureSuccessBenchmarkItems() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {FSB_ITEMS.map(({ Icon, title, subtitle }) => (
        <div key={title} className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray">
            <Icon className="h-6 w-6 text-lac-leman" />
          </div>
          <div>
            <h3 className="font-bold text-[#1A1E2E]">{title}</h3>
            <p className="mt-1 text-[#1A1E2E]">{subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function AmbidexterityVennDiagram() {
  return (
    <svg
      viewBox="0 0 420 310"
      className="w-full max-w-sm"
      aria-label="Ambidexterity Venn diagram: Perform and Transform circles with Ambidex Zone overlap">
      <defs>
        {/* Mask for left circle: show left circle area, hide the overlap with the right */}
        <mask id="u1-left-mask">
          <rect width="420" height="310" fill="black" />
          <circle cx="158" cy="165" r="128" fill="white" />
          <circle cx="268" cy="165" r="128" fill="black" />
        </mask>
        {/* Mask for right circle: show right circle area, hide the overlap with the left */}
        <mask id="u1-right-mask">
          <rect width="420" height="310" fill="black" />
          <circle cx="268" cy="165" r="128" fill="white" />
          <circle cx="158" cy="165" r="128" fill="black" />
        </mask>
      </defs>

      {/* Column labels */}
      <text
        x="118"
        y="30"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        letterSpacing="2"
        fill="#001E62"
        fontFamily="inherit">
        PERFORM
      </text>
      <text
        x="308"
        y="30"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        letterSpacing="2"
        fill="#001E62"
        fontFamily="inherit">
        TRANSFORM
      </text>

      {/* Left teal circle — overlap area is transparent, revealing the page background */}
      <circle cx="158" cy="165" r="128" fill="#1AAE9A" mask="url(#u1-left-mask)" />

      {/* Right purple circle — overlap area is transparent */}
      <circle cx="268" cy="165" r="128" fill="#5B4BAF" mask="url(#u1-right-mask)" />

      {/* Dot marker in the overlap */}
      <circle cx="213" cy="155" r="4.5" fill="#005DC4" />

      {/* Ambidex Zone label */}
      <text x="213" y="178" textAnchor="middle" fontSize="13" fill="#005DC4" fontFamily="inherit">
        Ambidex Zone
      </text>
    </svg>
  );
}

// ─── Leadership stream card ───────────────────────────────────────────────────

function LeadershipStream({ stream }: { stream: "AL" | "LS" | "OC" }) {
  const alSelected = stream === "AL";
  const lsSelected = stream === "LS";

  return (
    <div className="pt-16">
      <div className="inline-flex rounded-full bg-lac-leman/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-lac-leman">
        LEADERSHIP SPECIALISATION
      </div>
      <h2 className="mt-4 text-2xl font-bold text-[#1A1E2E]">Your Leadership Stream</h2>
      <p className="mt-3 leading-relaxed text-[#43576B]">
        Alongside the core programme, you will complete a leadership specialisation focused on a deeper area of personal
        development. At the start of your journey, you will complete a short diagnostic that will help you identify the
        specialisation most aligned with your interests and personal development goals. Use these results alongside your
        Talent Accelerator assessment results to inform your final choice.
      </p>

      {(stream === "AL" || stream === "LS") && (
        <div className="mt-3 flex items-center gap-2 text-[#43576B]">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-lac-leman" aria-hidden="true" />
          <p>
            Your Talent Accelerator results suggest that{" "}
            <strong>{alSelected ? "Adaptable Leadership" : "Leading Self"}</strong> may offer the greatest opportunity
            for development.
          </p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Adaptable Leadership */}
        <div
          className={`rounded-xl border p-5 ${alSelected ? "border-lac-leman bg-lac-leman/5" : "border-gray bg-white"}`}>
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-[#1A1E2E]">Adaptable Leadership</h3>
            {alSelected && (
              <span className="rounded-full bg-lac-leman/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-widest text-lac-leman">
                Recommended
              </span>
            )}
          </div>
          <p className="mt-1 leading-relaxed text-[#43576B]">
            Focuses on your ability to respond effectively to change, uncertainty and new situations. It explores how
            you adapt, learn and grow when faced with new challenges.
          </p>
        </div>

        {/* Leading Self */}
        <div
          className={`rounded-xl border p-5 ${lsSelected ? "border-lac-leman bg-lac-leman/5" : "border-gray bg-white"}`}>
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-[#1A1E2E]">Leading Self</h3>
            {lsSelected && (
              <span className="rounded-full bg-lac-leman/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-widest text-lac-leman">
                Recommended
              </span>
            )}
          </div>
          <p className="mt-1 leading-relaxed text-[#43576B]">
            Focuses on the personal foundations of effective leadership. It explores how you develop self-awareness,
            resilience and personal effectiveness to sustain high performance over time.
          </p>
        </div>
      </div>

      {stream === "OC" && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-lac-leman bg-lac-leman/5 px-4 py-5 text-[#43576B]">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-lac-leman" aria-hidden="true" />
          <p>
            Your Talent Accelerator results highlight development opportunities across <strong>both</strong> Leading
            Self and Adaptable Leadership.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Your Feedback ─────────────────────────────────────────────────────────────
type FeedbackKey = keyof Required<NonNullable<ProfileData["personalisedFeedback"]>>;

type PanelConfig = {
  key: FeedbackKey;
  title: string;
  Icon: ComponentType<{ className?: string }>;
};

const PANELS: PanelConfig[] = [
  { key: "leadingBusiness", title: "Leading Business", Icon: IconLeadingBusiness },
  { key: "leadingPeople", title: "Leading People", Icon: UsersIcon },
  { key: "leadingSelf", title: "Leading Self", Icon: UserIcon },
];

function YourFeedback({ data }: { data: ProfileData["personalisedFeedback"] }) {
  // Track which panels are open; keyed by feedback key. First panel open by default.
  const [open, setOpen] = useState<Partial<Record<FeedbackKey, boolean>>>({ leadingBusiness: true });
  const toggle = (key: FeedbackKey) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

  if (!data) return null; // Don't render if no feedback data is available

  return (
    <section className="flex w-full flex-col items-start gap-8">
      <h2 className="text-2xl font-bold leading-6 text-[#1A1E2E]">Your Feedback</h2>

      <div className="flex w-full flex-col items-start gap-6">
        {PANELS.map(({ key, title, Icon }) => (
          <FeedbackPanel
            key={key}
            title={title}
            Icon={Icon}
            body={data[key]}
            isOpen={open[key] ?? false}
            onToggle={() => toggle(key)}
          />
        ))}
      </div>
    </section>
  );
}

function FeedbackPanel({
  title,
  Icon,
  body,
  isOpen,
  onToggle,
}: {
  title: string;
  Icon: ComponentType<{ className?: string }>;
  body: string | undefined;
  isOpen: boolean;
  onToggle: () => void;
}) {
  if (!body) return null; // Don't render panel if no feedback is available
  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md px-4 py-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.04)] outline outline-1 -outline-offset-1 outline-gray">
      {/* Title bar (clickable toggle) */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Collapse" : "Expand"} ${title}`}
        className="flex w-full items-center gap-2 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman">
        <div className="flex flex-1 items-baseline">
          <div className="flex items-center gap-4">
            <Icon className="h-6 w-6 fill-lac-leman" />
            <span className="font-bold leading-7 text-[#1A1E2E]">{title}</span>
          </div>
        </div>
        <motion.span
          className="flex h-5 w-5 shrink-0 items-center justify-center text-[#1A1E2E]"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}>
          <ChevronDownIcon className="h-5 w-5" />
        </motion.span>
      </button>

      {/* Collapsible body */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="w-full overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}>
            <div className="flex w-full flex-col items-start gap-1">
              <p className="w-full leading-relaxed text-[#43576B]">{body}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
